import React, { useState } from 'react';
import { 
  User, Briefcase, GraduationCap, Mail, MapPin, 
  Calendar, Award, Code, Globe, X, Phone, Building
} from 'lucide-react';
import { colors } from './constants';
import { formatDate, formatGenre, calculateAge, getLatestExperience, calculateTotalExperienceYears } from './utils';

const ProfilePhoto = ({ demandeur }) => {
  if (demandeur.user && demandeur.user.photo) {
    return (
      <img 
        src={demandeur.user.photo} 
        alt={`${demandeur.user.first_name} ${demandeur.user.last_name}`} 
        className="h-full w-full object-cover rounded-full"
        onError={(e) => {
          e.target.onerror = null;
          e.target.style.display = 'none';
          e.target.parentElement.querySelector('.initials-fallback')?.classList.remove('hidden');
        }}
      />
    );
  }
  return null;
};

const InitialsFallback = ({ demandeur, hidden = false }) => (
  <div 
    className={`initials-fallback h-full w-full flex items-center justify-center text-white text-2xl font-medium rounded-full ${hidden ? 'hidden' : ''}`}
  >
    {demandeur.user.first_name?.charAt(0) || ''}{demandeur.user.last_name?.charAt(0) || ''}
  </div>
);

const DemandeurModal = ({ demandeur, onClose }) => {
  const [activeTab, setActiveTab] = useState('informations');
  
  if (!demandeur) return null;

  const latestExperience = getLatestExperience(demandeur.experiences);
  
  const tabs = [
    { id: 'informations', label: 'Informations', icon: User },
    { id: 'experiences', label: 'Expériences', icon: Briefcase },
    { id: 'formation', label: 'Formation', icon: GraduationCap },
    { id: 'diplomes', label: 'Diplômes', icon: Award },
    { id: 'competences', label: 'Compétences', icon: Code },
    { id: 'langues', label: 'Langues', icon: Globe },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto transform transition-all">
        {/* Header sticky */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <User className="h-5 w-5 mr-2" style={{color: colors.primary}} />
            Profil de {demandeur.user.first_name} {demandeur.user.last_name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6">
          {/* En-tête du profil */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-8 rounded-xl mb-8 shadow-md transform transition-all hover:shadow-lg">
            <div className="flex flex-col md:flex-row md:items-start">
              <div className="flex flex-col md:flex-row items-center mb-4 md:mb-0">
                <div className="h-28 w-28 rounded-full flex items-center justify-center shadow-md border-4 border-white overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` }}>
                  <ProfilePhoto demandeur={demandeur} />
                  <InitialsFallback demandeur={demandeur} hidden={!!demandeur.user?.photo} />
                </div>
                <div className="ml-0 md:ml-6 mt-4 md:mt-0 text-center md:text-left text-white">
                  <h2 className="text-2xl font-bold">
                    {demandeur.profile ? formatGenre(demandeur.profile.genre) : ''} {demandeur.user.first_name} {demandeur.user.last_name}
                  </h2>
                  
                  {latestExperience && (
                    <div className="flex items-center mt-1 text-orange-100">
                      <Briefcase size={16} className="mr-2" />
                      <p className="text-lg">{latestExperience.poste}</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <div className="bg-white bg-opacity-20 px-3 py-1 rounded-lg flex items-center">
                      <Calendar size={14} className="mr-1.5 text-white" />
                      <span className="text-white">
                        {demandeur.profile ? calculateAge(demandeur.profile.date_naissance) : '-'} ans
                      </span>
                    </div>
                    
                    <div className="bg-white bg-opacity-20 px-3 py-1 rounded-lg flex items-center">
                      <Briefcase size={14} className="mr-1.5 text-white" />
                      <span className="text-white">
                        {calculateTotalExperienceYears(demandeur.experiences)} an(s) d'expérience
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact rapide */}
              <div className="ml-0 md:ml-auto mt-4 md:mt-0 flex flex-col gap-2 bg-white bg-opacity-10 p-3 rounded-lg">
                <a href={`mailto:${demandeur.user.email}`} className="text-white flex items-center text-sm hover:bg-white hover:bg-opacity-20 p-2 rounded transition-colors">
                  <Mail size={16} className="mr-2" /> {demandeur.user.email}
                </a>
                <a href={`tel:${demandeur.user.telephone}`} className="text-white flex items-center text-sm hover:bg-white hover:bg-opacity-20 p-2 rounded transition-colors">
                  <Phone size={16} className="mr-2" /> {demandeur.user.telephone}
                </a>
              </div>
            </div>
          </div>
          
          {/* Navigation des sections */}
          <div className="flex overflow-x-auto mb-6 pb-2 border-b border-gray-200 gap-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 font-medium flex items-center whitespace-nowrap rounded-t-lg transition-colors ${
                    isActive 
                      ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50' 
                      : 'text-gray-600 hover:text-orange-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={16} className="mr-2" /> {tab.label}
                </button>
              );
            })}
          </div>
          
          {/* Informations personnelles */}
          {activeTab === 'informations' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <User size={20} className="mr-2" style={{color: colors.primary}} />
              Informations personnelles
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                  <Mail className="h-5 w-5" style={{color: colors.primary}} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-gray-800">{demandeur.user.email}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                  <Phone className="h-5 w-5" style={{color: colors.primary}} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Téléphone</p>
                  <p className="text-gray-800">{demandeur.user.telephone}</p>
                </div>
              </div>
              
              {demandeur.profile?.telephone2 && (
                <div className="flex items-start">
                  <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                    <Phone className="h-5 w-5" style={{color: colors.primary}} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Téléphone secondaire</p>
                    <p className="text-gray-800">{demandeur.profile?.telephone2}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-start">
                <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                  <MapPin className="h-5 w-5" style={{color: colors.primary}} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Adresse</p>
                  <p className="text-gray-800">{demandeur.profile?.adresse || 'Non renseigné'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                  <Calendar className="h-5 w-5" style={{color: colors.primary}} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Date et lieu de naissance</p>
                  <p className="text-gray-800">
                    {formatDate(demandeur.profile?.date_naissance)} à {demandeur.profile?.lieu_naissance || '-'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="p-2 rounded-full mr-3" style={{backgroundColor: colors.primaryLight}}>
                  <User className="h-5 w-5" style={{color: colors.primary}} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Situation matrimoniale</p>
                  <p className="text-gray-800">{demandeur.profile?.situation_familiale || 'Non renseigné'}</p>
                </div>
              </div>
            </div>
          </div>
          )}
          
          {/* Expériences professionnelles */}
          {activeTab === 'experiences' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <Briefcase size={20} className="mr-2" style={{color: colors.primary}} />
              Expériences professionnelles
            </h4>
            
            {demandeur.experiences.length > 0 ? (
              <div className="space-y-6">
                {demandeur.experiences.map(experience => (
                  <div key={experience.id} className="border-l-2 pl-4 py-3 transform transition hover:translate-x-1" style={{borderColor: colors.primary}}>
                    <div className="mb-2">
                      <h5 className="font-medium text-gray-900">{experience.poste}</h5>
                      <p className="text-gray-700 flex items-center">
                        <Building size={14} className="mr-2" style={{color: colors.primary}} />
                        {experience.nom_entreprise}
                      </p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Calendar size={14} className="mr-1.5" style={{color: colors.primary}} />
                      <span>
                        {formatDate(experience.date_debut)} - 
                        {experience.date_fin ? formatDate(experience.date_fin) : 'Présent'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <MapPin size={14} className="mr-1.5" style={{color: colors.primary}} />
                      <span>{experience.pays}</span>
                    </div>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{experience.description?.replace(/\\n/g, '\n')}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Briefcase size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Aucune expérience professionnelle renseignée</p>
              </div>
            )}
          </div>
          )}
          
          {/* Formations */}
          {activeTab === 'formation' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <GraduationCap size={20} className="mr-2" style={{color: colors.secondary}} />
              Formation
            </h4>
            
            {demandeur.formations.length > 0 ? (
              <div className="space-y-6">
                {demandeur.formations.map(formation => (
                  <div key={formation.id} className="border-l-2 pl-4 py-3 transform transition hover:translate-x-1" style={{borderColor: colors.secondary}}>
                    <div className="mb-2">
                      <h5 className="font-medium text-gray-900">{formation.nom}</h5>
                      <p className="text-gray-700">{formation.domaine_formation}</p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <Calendar size={14} className="mr-1.5" style={{color: colors.secondary}} />
                      <span>
                        {formatDate(formation.date_debut)} - 
                        {formation.date_fin ? formatDate(formation.date_fin) : 'En cours'}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <MapPin size={14} className="mr-1.5" style={{color: colors.secondary}} />
                      <span>{formation.pays}</span>
                    </div>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{formation.descrition?.replace(/\\n/g, '\n')}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <GraduationCap size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Aucune formation renseignée</p>
              </div>
            )}
          </div>
          )}
          
          {/* Diplômes */}
          {activeTab === 'diplomes' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <Award size={20} className="mr-2" style={{color: colors.primary}} />
              Diplômes
            </h4>
            
            {demandeur.diplomes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {demandeur.diplomes.map(diplome => (
                  <div key={diplome.id} className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow">
                    <h5 className="font-medium text-gray-900 flex items-center">
                      <Award size={16} className="mr-2" style={{color: colors.primary}} />
                      {diplome.type_diplome_nom}
                    </h5>
                    <p className="text-gray-600 mb-2">{diplome.etablissement}</p>
                    {diplome.domaine_formation_nom && (
                      <p className="text-sm mb-2">
                        <span className="px-2 py-1 rounded-full text-white text-xs" style={{backgroundColor: colors.primary}}>
                          {diplome.domaine_formation_nom}
                        </span>
                      </p>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 flex items-center">
                        <Calendar size={14} className="inline mr-1" />
                        {formatDate(diplome.date_obtention)}
                      </span>
                      <span className="text-gray-500 flex items-center">
                        <MapPin size={14} className="inline mr-1" />
                        {diplome.pays}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Award size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Aucun diplôme renseigné</p>
              </div>
            )}
          </div>
          )}
          
          {/* Compétences */}
          {activeTab === 'competences' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <Code size={20} className="mr-2" style={{color: colors.secondary}} />
              Compétences
            </h4>
            
            {demandeur.competences.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-2">
                {demandeur.competences.map((competence, index) => {
                  const isOrange = index % 2 === 0;
                  const bgColor = isOrange ? colors.primaryLight : colors.secondaryLight;
                  const textColor = isOrange ? colors.primary : colors.secondary;
                  
                  return (
                    <span 
                      key={competence.id}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: bgColor, color: textColor }}
                    >
                      {competence.description}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Code size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Aucune compétence renseignée</p>
              </div>
            )}
          </div>
          )}
          
          {/* Langues */}
          {activeTab === 'langues' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-800 pb-2 flex items-center">
              <Globe size={20} className="mr-2" style={{color: colors.primary}} />
              Langues
            </h4>
            
            {demandeur.langues.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {demandeur.langues.map(langue => {
                  let progressColor;
                  switch(langue.niveau_moyen) {
                    case 1: progressColor = 'bg-red-500'; break;
                    case 2: progressColor = 'bg-yellow-500'; break;
                    case 3: progressColor = 'bg-green-500'; break;
                    default: progressColor = 'bg-gray-500';
                  }
                  
                  const progressWidth = `${langue.niveau_moyen * 33}%`;
                  
                  return (
                    <div key={langue.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span className="font-medium text-gray-700 flex items-center">
                          <Globe size={14} className="mr-1.5" style={{color: colors.primary}} />
                          {langue.langue_nom}
                        </span>
                        <span className="text-sm py-0.5 px-2 rounded" style={{backgroundColor: colors.primaryLight, color: colors.primary}}>
                          {langue.niveau_label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                        <div 
                          className={`h-2 rounded-full ${progressColor}`}
                          style={{ width: progressWidth }}
                        ></div>
                      </div>
                      {langue.niveau_details && (
                        <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-gray-500">
                          <div>
                            <span>Parler: </span>
                            <span className="font-medium">{langue.niveau_details.parler_niveau}/3</span>
                          </div>
                          <div>
                            <span>Écrire: </span>
                            <span className="font-medium">{langue.niveau_details.ecrire_niveau}/3</span>
                          </div>
                          <div>
                            <span>Lire: </span>
                            <span className="font-medium">{langue.niveau_details.lire_niveau}/3</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Globe size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">Aucune langue renseignée</p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemandeurModal;
